import type { SearchExpression } from '../services/resourceDatasource';

/**
 * Convert an ag-Grid filter model into the server's column-filter SearchExpression —
 * the React port of dataGrid.ts:GetSearchExpressionFromFilterModel. Each active text
 * filter becomes a MultiTextField whose values carry the entered text and whose
 * comparisonType encodes the operator; the top-level fields are ANDed.
 *
 * Faithful to the Angular field/comparisonType serialization (MultiTextField.ts,
 * ComparisonType.ts, ListItem.ts). One deliberate difference: Angular applied the first
 * column's condition1/condition2 across every column (a latent bug for multi-column
 * combined filters); here each column's own conditions are used, which is identical for
 * the dominant single-column case and correct for the rest.
 */

// io.indexed.domainmodel.search.advanced.ComparisonType ordinals (ComparisonType.ts).
const enum ComparisonType {
  Equals = 0,
  NotEquals = 1,
  Contains = 2,
  StartsWith = 3,
  EndsWith = 4,
  NotContains = 8,
}

// io.indexed.domainmodel.search.advanced.Operator ordinals (SearchExpression.ts).
const OP_AND = 0;

const MULTI_TEXT_FIELD = 'io.indexed.domainmodel.search.advanced.MultiTextField';

const TYPE_TO_COMPARISON: Record<string, ComparisonType> = {
  contains: ComparisonType.Contains,
  notContains: ComparisonType.NotContains,
  equals: ComparisonType.Equals,
  notEquals: ComparisonType.NotEquals,
  startsWith: ComparisonType.StartsWith,
  endsWith: ComparisonType.EndsWith,
};

function uuid(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : '';
}

function multiTextField(colId: string, filter: string, comparisonType: ComparisonType) {
  return {
    name: colId,
    description: '',
    comparisonType,
    __className: MULTI_TEXT_FIELD,
    __uuid: uuid(),
    values: [{ id: filter, name: filter }],
  };
}

interface AgTextCondition {
  type?: string;
  filter?: string;
}
interface AgColumnFilter extends AgTextCondition {
  operator?: 'AND' | 'OR';
  condition1?: AgTextCondition;
  condition2?: AgTextCondition;
}

export function filterModelToSearchExpression(
  filterModel: Record<string, AgColumnFilter> | null | undefined
): SearchExpression | undefined {
  const entries = Object.entries(filterModel ?? {});
  if (entries.length === 0) return undefined;

  const fields: unknown[] = [];
  const push = (colId: string, cond?: AgTextCondition) => {
    if (!cond?.type || cond.filter == null || cond.filter === '') return;
    const comparison = TYPE_TO_COMPARISON[cond.type];
    if (comparison === undefined) return;
    fields.push(multiTextField(colId, cond.filter, comparison));
  };

  for (const [colId, model] of entries) {
    if (model.operator && (model.condition1 || model.condition2)) {
      push(colId, model.condition1);
      push(colId, model.condition2);
    } else {
      push(colId, model);
    }
  }

  if (fields.length === 0) return undefined;
  return { operator: OP_AND, fields, searchExpressions: [] };
}
